import { FluzElementsError } from "./errors.js";
import { createEnvelope, isElementsMessage } from "./protocol.js";
import type { ElementsMessage, ElementsMessageType } from "./protocol.js";
import { validateStyle } from "./style.js";
import type { StyleAllowlist } from "./style.js";

export interface FrameMountOptions {
  mountId: string;
  frameUrl: string;
  frameOrigin: string;
  container: HTMLElement;
  style?: StyleAllowlist;
  mountTimeoutMs?: number;
  onError?: (error: FluzElementsError) => void;
  handshakeExtra?: Record<string, unknown>;
  onMessage?: (message: ElementsMessage) => void;
}

export interface FrameMount {
  readonly iframe: HTMLIFrameElement;
  send(type: ElementsMessageType, payload: unknown): void;
  destroy(): void;
}

const DEFAULT_MOUNT_TIMEOUT_MS = 10000;

export function mountFrame(options: FrameMountOptions): Promise<FrameMount> {
  const { mountId, frameUrl, frameOrigin, container, mountTimeoutMs = DEFAULT_MOUNT_TIMEOUT_MS, onError } = options;
  const validatedStyle = options.style ? validateStyle(options.style) : undefined;

  const iframe = document.createElement("iframe");
  iframe.src = frameUrl;
  iframe.style.border = "none";
  iframe.setAttribute("title", "Fluz secure element");
  iframe.setAttribute("sandbox", "allow-scripts allow-same-origin allow-forms");
  iframe.referrerPolicy = "no-referrer";

  return new Promise<FrameMount>((resolve, reject) => {
    let settled = false;

    function onMessage(event: MessageEvent) {
      if (event.origin !== frameOrigin || event.source !== iframe.contentWindow) {
        return;
      }

      const data: unknown = event.data;
      if (!isElementsMessage(data) || data.mountId !== mountId) {
        return;
      }

      if (data.type === "handshake-ack") {
        settleSuccess();
        return;
      }

      if (data.type === "error" && onError) {
        const payload = data.payload;
        if (payload && typeof payload === "object" && (payload as { code?: unknown }).code === "rate_limited") {
          const retryAfterSeconds = (payload as { retryAfterSeconds?: unknown }).retryAfterSeconds;
          const message =
            typeof retryAfterSeconds === "number"
              ? `Rate limit exceeded. Retry after ${retryAfterSeconds}s.`
              : "Rate limit exceeded.";
          onError(new FluzElementsError("RATE_LIMITED", message));
          return;
        }

        const message = typeof payload === "string" ? payload : "Field error";
        onError(new FluzElementsError("FIELD_ERROR", message));
        return;
      }

      options.onMessage?.(data);
    }

    function teardown() {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timeoutId);
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }

    function settleSuccess() {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);
      resolve({
        iframe,
        send(type, payload) {
          iframe.contentWindow?.postMessage(createEnvelope(mountId, type, payload), frameOrigin);
        },
        destroy: teardown,
      });
    }

    function settleFailure(error: FluzElementsError) {
      if (settled) {
        return;
      }
      settled = true;
      teardown();
      reject(error);
    }

    const timeoutId = window.setTimeout(() => {
      settleFailure(new FluzElementsError("MOUNT_TIMEOUT", `Mount timed out waiting for handshake-ack (${mountId})`));
    }, mountTimeoutMs);

    window.addEventListener("message", onMessage);

    iframe.addEventListener("load", () => {
      iframe.contentWindow?.postMessage(
        createEnvelope(mountId, "handshake", { style: validatedStyle, ...options.handshakeExtra }),
        frameOrigin,
      );
    });

    iframe.addEventListener("error", () => {
      settleFailure(new FluzElementsError("MOUNT_FAILED", `Failed to load frame for mount ${mountId}`));
    });

    container.appendChild(iframe);
  });
}
